/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: ~154 lines
 * - Adding: ~4 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_DETAILS_PAGE
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Main Broadcast Details Page component displaying broadcast item information
 * and company contact details in a two-column layout following ItemDetailPage pattern.
 *
 * File Size Check:
 * - Current: 0 lines (new file)
 * - Projected: ~170 lines
 * - Decision: Proceed (within 200-line limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcast-details/components/
 * - client/src/services/broadcastService.ts
 * - client/src/features/dashboard/DashboardBroadcastTable.tsx (navigation source)
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Spinner, Alert, Button } from "react-bootstrap";
import { getBroadcastById } from "../../../../services/broadcastService";
import { BroadcastDetail, BroadcastUser } from "./interfaces/broadcastDetail";
import { notify } from "../../../../utils/notifications";
import BroadcastInformation from "./components/BroadcastInformation";
import BroadcastCompanyInfo from "./components/BroadcastCompanyInfo";
import styles from "./BroadcastDetailsPage.module.scss";

// [2025-12-29] Broadcast details: Main page component with two-column layout

const BroadcastDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [broadcast, setBroadcast] = useState<BroadcastDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // [2025-12-29] Fetch broadcast data on mount
  useEffect(() => {
    const fetchBroadcast = async () => {
      if (!id) {
        setError("Broadcast ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await getBroadcastById(id);
        const broadcastData = response.data.data;

        // [2025-12-29] Transform to BroadcastDetail format
        const detail: BroadcastDetail = {
          _id: broadcastData._id,
          userId: broadcastData.userId as BroadcastUser,
          type: broadcastData.type,
          broadcastNo: broadcastData.broadcastNo,
          broadcast: broadcastData.broadcast,
          status: broadcastData.status,
          sentAt: broadcastData.sentAt,
          createdAt: broadcastData.createdAt,
          updatedAt: broadcastData.updatedAt,
        };

        setBroadcast(detail);
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || "We couldn't load this broadcast. Please refresh the page";
        setError(errorMessage);
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(err, "We couldn't load this broadcast. Please refresh the page");
      } finally {
        setLoading(false);
      }
    };

    fetchBroadcast();
  }, [id]);

  // [2025-12-29] Back button handler
  const handleBack = () => {
    navigate(-1);
  };

  // [2025-12-29] Loading state
  if (loading) {
    return (
      <Container className={styles["broadcast-details-page"]}>
        <div className={styles["broadcast-details-page__header"]}>
          <Button variant="outline-secondary" onClick={handleBack}>
            ← Back
          </Button>
        </div>
        <div className={styles["broadcast-details-page__loading"]}>
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  // [2025-12-29] Error state
  if (error || !broadcast) {
    return (
      <Container className={styles["broadcast-details-page"]}>
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error || "Broadcast not found"}</p>
          <Button variant="outline-danger" onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </Alert>
      </Container>
    );
  }

  // [2025-12-29] Get first item for display (broadcasts typically have 1+ items)
  const firstItem = broadcast.broadcast[0];
  
  // [2025-12-29] Build title: "WTB: DJI ESC 6S-40A"
  const title = `${broadcast.type}: ${firstItem.manufacturer} ${firstItem.partNumber || firstItem.model || ""}`.trim();

  return (
    <Container className={styles["broadcast-details-page"]}>
      {/* Back button */}
      <div className={styles["broadcast-details-page__header"]}>
        <Button variant="outline-secondary" onClick={handleBack}>
          ← Back
        </Button>
      </div>

      {/* Title */}
      <h1 className={styles["broadcast-details-page__title"]}>{title}</h1>

      {/* Two-column layout */}
      <div className={styles["broadcast-details-page__grid"]}>
        {/* Left: Broadcast Information */}
        <div className={styles["broadcast-details-page__col"]}>
          <BroadcastInformation item={firstItem} />
        </div>

        {/* Right: Company Information */}
        <div className={styles["broadcast-details-page__col"]}>
          <BroadcastCompanyInfo user={broadcast.userId} />
        </div>
      </div>
    </Container>
  );
};

export default BroadcastDetailsPage;
